// ═══════════════════════════════════════════════════════════════
// About / How To — OGEMC Tampa Debris Operations
// Landing page explaining the tool and step-by-step workflow
// ═══════════════════════════════════════════════════════════════

import { useNavigate } from 'react-router-dom';
import {
  IconLayoutDashboard, IconFileImport, IconBuildingFactory2,
  IconTruck, IconTicket, IconScale, IconPackage, IconArrowRight,
  IconAlertTriangle, IconCertificate, IconClipboardList,
  IconClock, IconFileInvoice, IconInfoCircle,
} from '@tabler/icons-react';

// ── Step definition ──────────────────────────────────────────────

interface Step {
  number: number;
  label: string;
  description: string;
  details: string[];
  icon: React.ComponentType<{ size?: number; color?: string }>;
  color: string;
  route?: string;
  routeLabel?: string;
}

const STEPS: Step[] = [
  {
    number: 1,
    label: 'Select Your Active Incident',
    description: 'All data in the system is scoped to the active incident. Start by selecting the correct disaster event from the incident picker in the top bar.',
    details: [
      'Click the incident selector pill in the top navigation bar',
      'Choose the declared disaster (e.g., Hurricane Milton DR-4816)',
      'Every page — tickets, contractors, invoices, FEMA packages — will reflect only that incident\'s data',
    ],
    icon: IconLayoutDashboard,
    color: '#4B3FFF',
  },
  {
    number: 2,
    label: 'Import the ERC Roster',
    description: 'Upload the ERC (Emergency Response Crew) personnel roster so the system can auto-fill Force Account labor entries and validate crew assignments.',
    details: [
      'Navigate to Debris → Personnel Roster',
      'Import the current CSV roster export from HR or the ERC coordinator',
      'Verify employee IDs, pay rates, and ERC district assignments are correct',
      'The roster powers auto-fill in Force Account and Bulk Generate',
    ],
    icon: IconFileImport,
    color: '#0891b2',
    route: '/debris/personnel',
    routeLabel: 'Go to Personnel Roster',
  },
  {
    number: 3,
    label: 'Onboard Contractors & Certify Trucks',
    description: 'Add debris hauling contractors and certify their truck fleet before any field work begins. Uncertified trucks cannot be reimbursed under FEMA PA.',
    details: [
      'Navigate to Debris → Contractors to add each hauling firm',
      'Navigate to Debris → Truck Registry to register each vehicle',
      'Upload FDEP/TDEM certification PDFs for each truck (required for FEMA)',
      'Verify capacity (CY), placard number, and expiration date for every truck',
      'Trucks are automatically scoped to the active incident\'s contractors — certs from past events will not appear',
    ],
    icon: IconTruck,
    color: '#7c3aed',
    route: '/debris/trucks',
    routeLabel: 'Go to Truck Registry',
  },
  {
    number: 4,
    label: 'Process Incoming Load Tickets',
    description: 'Load tickets are the core unit of debris tracking. They come in through the field portal, paper scanning, or manual entry.',
    details: [
      'Field crews create tickets during operations; paper tickets can be scanned and OCR-processed via Data Intake',
      'Paper tickets can be uploaded and OCR-processed via Debris → Data Intake',
      'Review OCR-extracted tickets in the Data Intake queue — verify truck number, load %, and disposal site before approving',
      'Approved tickets flow automatically into Load Tickets and are available for invoicing',
    ],
    icon: IconTicket,
    color: '#d97706',
    route: '/debris/processor',
    routeLabel: 'Go to Data Intake',
  },
  {
    number: 5,
    label: 'Review & Reconcile Invoices',
    description: 'Contractors submit invoices against load tickets. Reconciliation matches contractor-submitted loads to field-verified ticket data and flags variances.',
    details: [
      'Navigate to Debris → Invoices to log contractor invoice submissions',
      'Navigate to Debris → Reconciliation to run variance analysis',
      'Variances > 5% are flagged for review — investigate the specific tickets',
      'Approved reconciliations feed into FEMA package cost calculations',
    ],
    icon: IconScale,
    color: '#16a34a',
    route: '/debris/reconciliation',
    routeLabel: 'Go to Reconciliation',
  },
  {
    number: 6,
    label: 'Track Force Account Labor & Equipment',
    description: 'City employees and city-owned equipment used for debris operations are tracked separately from contractors as "Force Account" costs.',
    details: [
      'Navigate to Debris → Force Account',
      'Use Bulk Generate to create labor records for all ERC crew members at once',
      'Enter regular hours, overtime hours, and base hourly rate for each shift',
      'Apply night, weekend, and holiday differentials as applicable',
      'Equipment entries use FEMA 2025 rate schedules for reimbursable hourly rates',
    ],
    icon: IconClock,
    color: '#9333ea',
    route: '/debris/forceaccount',
    routeLabel: 'Go to Force Account',
  },
  {
    number: 7,
    label: 'Generate FEMA PA Packages',
    description: 'When documentation is complete, generate a FEMA-ready submission package. The system compiles all tickets, certs, invoices, and force account records into a structured ZIP.',
    details: [
      'Navigate to Debris → FEMA Packages',
      'Create a new package and select the billing period and categories (Cat A / Cat B)',
      'Review the pre-submission checklist — all red items must be resolved before submitting',
      'Generate the ZIP package containing the HTML summary, ticket index, truck manifest, and supporting docs',
      'Upload the package to FEMA\'s Grants Portal or provide to your PA Coordinator',
    ],
    icon: IconPackage,
    color: '#D33423',
    route: '/debris/fema',
    routeLabel: 'Go to FEMA Packages',
  },
];

// ── Tips ─────────────────────────────────────────────────────────

const TIPS = [
  {
    icon: IconAlertTriangle,
    color: '#d97706',
    title: 'Incident scoping is critical',
    body: 'Always verify the active incident before entering data. Contractors, tickets, invoices, and force account records are all tied to a specific incident. Data entered under the wrong incident cannot be easily moved.',
  },
  {
    icon: IconCertificate,
    color: '#4B3FFF',
    title: 'Truck certs must be current',
    body: 'FEMA will disallow reimbursement for loads hauled by uncertified or expired trucks. Check the Truck Registry before each operational period and renew certs before expiration.',
  },
  {
    icon: IconClipboardList,
    color: '#037730',
    title: 'Document everything',
    body: 'Attach source documents (time sheets, invoices, inspection reports) directly to Force Account entries and FEMA package line items using the paperclip attachment on each row. FEMA auditors require source documentation for all claimed costs.',
  },
  {
    icon: IconFileInvoice,
    color: '#0891b2',
    title: 'Reconcile before packaging',
    body: 'Always complete reconciliation before generating a FEMA package. Unreconciled invoices will not be included in cost totals, and the pre-submission checklist will flag the issue.',
  },
];

// ════════════════════════════════════════════════════════════════
// Component
// ════════════════════════════════════════════════════════════════

export default function About() {
  const navigate = useNavigate();

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px 64px' }}>

      {/* ── Header ─────────────────────────────────────────────── */}
      <div style={{ marginBottom: 40, borderBottom: '2px solid #e2e8f0', paddingBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, #4B3FFF, #2C01D6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <IconInfoCircle size={26} color="white" />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
              About This System
            </h1>
            <p style={{ margin: 0, fontSize: 14, color: '#64748b', marginTop: 2 }}>
              OpenGov Emergency Management Console — Debris Operations · City of Tampa
            </p>
          </div>
        </div>

        <p style={{ fontSize: 15, color: '#334155', lineHeight: 1.7, maxWidth: 760, margin: '20px 0 24px' }}>
          This system is the City of Tampa's central platform for managing debris operations under a federally declared disaster.
          It tracks load tickets, contractor certifications, disposal sites, force account labor, and generates FEMA Public Assistance
          documentation packages for cost recovery.
        </p>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/debris/dashboard')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: '#4B3FFF', color: 'white', fontWeight: 600, fontSize: 14,
              transition: 'background 150ms',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#2C01D6'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#4B3FFF'; }}
          >
            Go to Dashboard <IconArrowRight size={16} />
          </button>
        </div>
      </div>

      {/* ── Step-by-step workflow ────────────────────────────────── */}
      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>
        Step-by-Step Workflow
      </h2>
      <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 24px' }}>
        Follow these steps in order at the start of each declared disaster. Steps may repeat each operational period.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 48 }}>
        {STEPS.map((step, i) => {
          const Icon = step.icon;
          return (
            <div
              key={step.number}
              style={{
                display: 'flex', gap: 16, padding: '20px 20px 20px 16px',
                borderRadius: 12, border: '1px solid #e2e8f0',
                background: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                borderLeft: `4px solid ${step.color}`,
              }}
            >
              {/* Step number + icon */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: step.color + '18',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={20} color={step.color} />
                </div>
                {i < STEPS.length - 1 && (
                  <div style={{ width: 2, flex: 1, minHeight: 16, background: '#e2e8f0', borderRadius: 2 }} />
                )}
              </div>

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: step.color,
                    background: step.color + '18', padding: '2px 8px', borderRadius: 20,
                    letterSpacing: '0.05em', textTransform: 'uppercase',
                  }}>
                    Step {step.number}
                  </span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>{step.label}</span>
                </div>
                <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, margin: '0 0 10px' }}>
                  {step.description}
                </p>
                <ul style={{ margin: '0 0 12px', paddingLeft: 20 }}>
                  {step.details.map((d, j) => (
                    <li key={j} style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6, marginBottom: 3 }}>
                      {d}
                    </li>
                  ))}
                </ul>
                {step.route && (
                  <button
                    onClick={() => navigate(step.route!)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '6px 14px', borderRadius: 6, border: `1.5px solid ${step.color}`,
                      background: 'white', color: step.color, fontWeight: 600, fontSize: 12,
                      cursor: 'pointer', transition: 'background 150ms',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = step.color + '12'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'white'; }}
                  >
                    {step.routeLabel} <IconArrowRight size={12} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Tips & reminders ─────────────────────────────────────── */}
      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>
        Important Reminders
      </h2>
      <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 20px' }}>
        Common pitfalls and things to keep in mind during operations.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 14, marginBottom: 48 }}>
        {TIPS.map((tip, i) => {
          const Icon = tip.icon;
          return (
            <div
              key={i}
              style={{
                padding: '16px 18px', borderRadius: 10,
                border: '1px solid #e2e8f0', background: 'white',
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                borderTop: `3px solid ${tip.color}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <Icon size={16} color={tip.color} />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{tip.title}</span>
              </div>
              <p style={{ margin: 0, fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>{tip.body}</p>
            </div>
          );
        })}
      </div>

      {/* ── Module quick reference ───────────────────────────────── */}
      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>
        Module Quick Reference
      </h2>
      <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 20px' }}>
        What each section of the Debris module is for.
      </p>

      <div style={{
        borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden',
        fontSize: 13, marginBottom: 48,
      }}>
        {[
          { name: 'Dashboard',        path: '/debris',                desc: 'KPIs, action queue, pipeline health, recent activity' },
          { name: 'Data Intake',       path: '/debris/processor',     desc: 'OCR scan and review of paper load tickets; confidence-based routing' },
          { name: 'Load Tickets',     path: '/debris/tickets',        desc: 'All approved load tickets — search, filter, view, edit' },
          { name: 'Disposal Sites',   path: '/debris/sites',          desc: 'Register and manage TDEM-permitted disposal/reduction sites' },
          { name: 'Truck Registry',   path: '/debris/trucks',         desc: 'Truck certifications scoped to the active incident\'s contractors' },
          { name: 'Contractors',      path: '/debris/contractors',    desc: 'Debris hauling firms, contract amounts, and contact information' },
          { name: 'Reconciliation',   path: '/debris/reconciliation', desc: 'Match contractor invoices to field-verified tickets; flag variances' },
          { name: 'Invoices',         path: '/debris/invoices',       desc: 'Contractor invoice log and payment status tracking' },
          { name: 'Force Account',    path: '/debris/forceaccount',   desc: 'City employee labor and owned-equipment costs for FEMA reimbursement' },
          { name: 'FEMA Packages',    path: '/debris/fema',           desc: 'Build and export FEMA PA submission packages (Cat A / Cat B)' },
          { name: 'PW Register',      path: '/debris/pw',             desc: 'Project Worksheet register and cost tracking per PW number' },
          { name: 'Personnel Roster', path: '/debris/personnel',      desc: 'Import and manage the ERC roster for force account auto-fill' },
          { name: 'Analytics',        path: '/debris/analytics',      desc: 'Charts and trend analysis across load volumes, costs, and contractors' },
          { name: 'Documentation',    path: '/debris/evidence',       desc: 'Centralized document storage for audit-ready FEMA documentation files' },
          { name: 'Pre-Sub Audit',    path: '/debris/audit',          desc: 'Pre-submission checklist — catch missing docs before filing' },
          { name: 'Doc Requirements', path: '/debris/document-requirements', desc: 'Required documentation checklist auto-populated from incident data' },
        ].map((row, i) => (
          <div
            key={row.path}
            onClick={() => navigate(row.path)}
            style={{
              display: 'flex', alignItems: 'baseline', gap: 16,
              padding: '11px 16px', cursor: 'pointer',
              background: i % 2 === 0 ? 'white' : '#f8fafc',
              borderBottom: i < 15 ? '1px solid #f1f5f9' : 'none',
              transition: 'background 100ms',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = '#f0f4ff'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = i % 2 === 0 ? 'white' : '#f8fafc'; }}
          >
            <span style={{ fontWeight: 600, color: '#4B3FFF', minWidth: 160, flexShrink: 0 }}>{row.name}</span>
            <span style={{ color: '#64748b' }}>{row.desc}</span>
          </div>
        ))}
      </div>

      {/* ── Footer ──────────────────────────────────────────────── */}
      <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 12, paddingTop: 24, borderTop: '1px solid #e2e8f0' }}>
        OpenGov Emergency Management Console · City of Tampa, FL ·{' '}
        <span style={{ color: '#4B3FFF', cursor: 'pointer' }} onClick={() => navigate('/debris/dashboard')}>
          Go to Dashboard →
        </span>
      </div>

    </div>
  );
}
